import { Box, Flex, Text } from 'exsportia-components'
import React, { FC } from 'react'

import { FieldProps, Icons } from './types'

// exsportia-components' Box and Flex support `overflow` and nothing axis-specific — the string
// "overflowX" does not appear anywhere in the built package — so an `overflowX`/`overflowY` PROP is
// dropped in silence. Passed through `style`, which does reach the DOM (Dima 2026-08-30).
// This capped list could not be scrolled at all, so options below 250px were unreachable.
const scrollYStyle: React.CSSProperties = {overflowY: 'auto'}

export const MultipleChoiceField: FC<FieldProps> = ({ qId, options, value, onChange, theme }) => {
  return (
    <Flex flexDirection='column' gap='10px'>
      {options?.map((opt) => (
        <Flex
          key={opt.id}
          as='label'
          alignItems='center'
          cursor='pointer'
          p='12px 16px'
          borderRadius='12px'
          bg={value === opt.id ? `${theme.colors.primary}08` : 'transparent'}
          border={`1px solid ${value === opt.id ? theme.colors.primary : theme.colors.border}`}
          transition='all 0.2s'
          hover={{ bg: `${theme.colors.primary}05` }}
        >
          <input
            type='radio'
            name={qId}
            value={opt.id}
            checked={value === opt.id}
            onChange={() => onChange(qId, opt.id)}
            style={{ marginRight: '12px', accentColor: theme.colors.primary, width: '18px', height: '18px' }}
          />
          <Text settings={{ fontSize: '15px', color: theme.colors.secondary }}>{opt.label}</Text>
        </Flex>
      ))}
    </Flex>
  )
}

export const CheckboxesField: FC<FieldProps> = ({ qId, question, options, value, onChange, theme }) => {
  const { config } = question
  const currentValues = (Array.isArray(value) ? value : []) as string[]
  const minSelected = Number(config?.min) || 0
  const maxSelected = Number(config?.max) || Infinity

  const toggleValue = (id: string) => {
    if (currentValues.includes(id)) {
      onChange(qId, currentValues.filter((v: string) => v !== id))
    } else {
      if (currentValues.length < maxSelected) {
        onChange(qId, [...currentValues, id])
      }
    }
  }

  return (
    <Flex flexDirection='column' gap='10px'>
      {options?.map((opt) => {
        const isChecked = currentValues.includes(opt.id)
        const isDisabled = !isChecked && currentValues.length >= maxSelected
        return (
          <Flex
            key={opt.id}
            as='label'
            alignItems='center'
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            p='12px 16px'
            borderRadius='12px'
            bg={isChecked ? `${theme.colors.primary}08` : 'transparent'}
            border={`1px solid ${isChecked ? theme.colors.primary : theme.colors.border}`}
            transition='all 0.2s'
            opacity={isDisabled ? 0.6 : 1}
            hover={!isDisabled ? { bg: `${theme.colors.primary}05` } : undefined}
          >
            <input
              type='checkbox'
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => toggleValue(opt.id)}
              style={{ marginRight: '12px', accentColor: theme.colors.primary, width: '18px', height: '18px' }}
            />
            <Text settings={{ fontSize: '15px', color: theme.colors.secondary }}>{opt.label}</Text>
          </Flex>
        )
      })}
      {(minSelected > 0 || maxSelected < Infinity) && (
        <Box mt='4px'>
          <Text settings={{ fontSize: '12px', color: theme.colors.gray }}>
            {minSelected > 0 && maxSelected < Infinity
              ? `Select between ${minSelected} and ${maxSelected} options`
              : minSelected > 0
                ? `Select at least ${minSelected} options`
                : `Select up to ${maxSelected} options`}
          </Text>
        </Box>
      )}
    </Flex>
  )
}

type DropdownFieldProps = FieldProps & {
  icons: Icons
  isDropdownOpen: boolean
  setIsDropdownOpen: (open: boolean) => void
}

export const DropdownField: FC<DropdownFieldProps> = ({
  qId,
  question,
  options,
  value,
  onChange,
  theme,
  icons,
  isDropdownOpen,
  setIsDropdownOpen
}) => {
  const { config } = question
  const isMulti = !!config?.multi
  const selectedIds = isMulti ? (Array.isArray(value) ? (value as string[]) : []) : ([value].filter(Boolean) as string[])
  const selectedOptions = options?.filter(opt => selectedIds.includes(opt.id)) || []

  return (
    <Box position='relative'>
      <Flex
        alignItems='center'
        justifyContent='space-between'
        p='0 16px'
        borderRadius='12px'
        border={`1px solid ${selectedOptions.length === 0 && !isDropdownOpen ? theme.colors.border : theme.colors.primary}`}
        bg={theme.colors.white}
        height={isMulti && selectedOptions.length > 0 ? 'auto' : '48px'}
        minHeight='48px'
        cursor='pointer'
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        // The trigger is a div, so without an explicit tabIndex the browser skips it and
        // TAB jumps straight from the previous field to the next one (WBA-4224).
        tabIndex={0}
        role='combobox'
        aria-expanded={isDropdownOpen}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsDropdownOpen(!isDropdownOpen)
            return
          }
          if (e.key === 'Escape' && isDropdownOpen) {
            setIsDropdownOpen(false)
          }
        }}
        style={{ transition: 'all 0.2s' }}
        hover={{ borderColor: theme.colors.primary }}
      >
        <Flex flex={1} gap='6px' flexWrap='wrap' p={isMulti && selectedOptions.length > 0 ? '8px 0' : '0'}>
          {selectedOptions.length > 0 ? (
            isMulti ? (
              selectedOptions.map(opt => (
                <Flex
                  key={opt.id}
                  bg={`${theme.colors.primary}15`}
                  p='2px 8px'
                  borderRadius='6px'
                  alignItems='center'
                  gap='4px'
                >
                  <Text settings={{ fontSize: '13px', color: theme.colors.primary, fontWeight: '600' }}>{opt.label}</Text>
                  <Box
                    style={{ display: 'flex', alignItems: 'center', marginTop: '-8px' }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      onChange(qId, selectedIds.filter(id => id !== opt.id))
                    }}
                  >
                    {icons.close({ w: '10px', h: '10px', color: theme.colors.primary })}
                  </Box>
                </Flex>
              ))
            ) : (
              <Text settings={{ fontSize: '15px', color: theme.colors.secondary }}>{selectedOptions[0].label}</Text>
            )
          ) : (
            <Text settings={{ fontSize: '15px', color: theme.colors.gray }}>Select an option</Text>
          )}
        </Flex>
        <Box transform={isDropdownOpen ? 'rotate(180deg)' : 'none'} style={{ transition: 'transform 0.2s' }}>
          {icons.chevronDown({ w: '20px', h: '20px', color: theme.colors.gray })}
        </Box>
      </Flex>

      {isDropdownOpen && (
        <>
          <Box
            position='fixed'
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={99}
            onClick={() => setIsDropdownOpen(false)}
          />
          <Box
            position='absolute'
            top='100%'
            left={0}
            right={0}
            bg={theme.colors.white || '#ffffff'}
            borderRadius='12px'
            boxShadow='0 10px 25px rgba(0,0,0,0.15)'
            border={`1px solid ${theme.colors.border || '#e0e0e0'}`}
            zIndex={2000}
            maxHeight='250px'
            style={scrollYStyle}
            mt='4px'
          >
            {options?.map((opt) => {
              const isSelected = selectedIds.includes(opt.id)
              return (
                <Flex
                  key={opt.id}
                  p='12px 16px'
                  alignItems='center'
                  justifyContent='space-between'
                  cursor='pointer'
                  bg={isSelected ? `${theme.colors.primary}08` : 'transparent'}
                  hover={{ bg: `${theme.colors.bgGray}40` }}
                  onClick={() => {
                    if (isMulti) {
                      const next = isSelected
                        ? selectedIds.filter(id => id !== opt.id)
                        : [...selectedIds, opt.id]
                      onChange(qId, next)
                    } else {
                      onChange(qId, opt.id)
                      setIsDropdownOpen(false)
                    }
                  }}
                >
                  <Text settings={{
                    fontSize: '15px',
                    color: isSelected ? theme.colors.primary : theme.colors.secondary,
                    fontWeight: isSelected ? '600' : '400'
                  }}>
                    {opt.label}
                  </Text>
                  {isSelected && icons.check({ w: '18px', h: '18px', color: theme.colors.primary })}
                </Flex>
              )
            })}
          </Box>
        </>
      )}
    </Box>
  )
}
